import React, { useRef } from "react";

interface DatePickerProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
	label?: string;
	error?: string;
	fullWidth?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
	label,
	error,
	fullWidth = true,
	className = "",
	id,
	value,
	onChange,
	...props
}) => {
	const inputId =
		id ||
		(label ? `date-${label.toLowerCase().replace(/\s+/g, "-")}` : undefined);
	const inputRef = useRef<HTMLInputElement>(null);

	// Convert Date object to YYYY-MM-DD format for input
	const formatDateValue = (
		val: string | number | readonly string[] | Date | null | undefined
	): string => {
		if (!val) return "";
		if (val instanceof Date) {
			return val.toISOString().split("T")[0];
		}
		return String(val);
	};

	const baseInputClasses =
		"shadow appearance-none border rounded-lg py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 bg-white shadow-sm hover:shadow-md select-none";
	const errorInputClasses = error ? "border-red-400 focus:ring-red-500" : "border-gray-300";
	const widthClasses = fullWidth ? "w-full" : "";
	const inputClasses = `${baseInputClasses} ${errorInputClasses} ${widthClasses} ${className}`;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(e);
		}
	};

	const handleContainerClick = () => {
		if (inputRef.current && !props.disabled) {
			inputRef.current.showPicker?.();
			inputRef.current.focus();
		}
	};

	return (
		<div 
			className={`${fullWidth ? "w-full" : ""} select-none`}
			onClick={handleContainerClick}
			onMouseDown={(e) => {
				// Prevent text selection when clicking on container
				if (e.target !== inputRef.current) {
					e.preventDefault();
				}
			}}
		>
			{label && (
				<label
					htmlFor={inputId}
					className="block text-gray-700 text-sm font-bold mb-2 cursor-pointer"
				>
					{label}
				</label>
			)}
			<input
				ref={inputRef}
				id={inputId}
				type="date"
				value={formatDateValue(value)}
				onChange={handleChange}
				onSelect={(e) => e.preventDefault()}
				onMouseDown={(e) => {
					// Prevent text selection, but allow the picker to open
					if (e.target === inputRef.current) {
						e.preventDefault();
						inputRef.current?.showPicker?.();
						inputRef.current?.focus();
					}
				}}
				className={inputClasses}
				{...props}
			/>
			{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
		</div>
	);
};

export default DatePicker;
